/**
 * Get cropped image as Blob from image element and percent crop.
 * Used for profile picture upload with square crop.
 */

export interface PercentCropBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Map percent crop (react-image-crop, relative to the img layout box) to natural
 * pixels when the bitmap is shown with object-fit: contain (letterboxing).
 */
function percentCropToNaturalRect(
  image: HTMLImageElement,
  crop: PercentCropBox
): { x: number; y: number; w: number; h: number } {
  const nw = image.naturalWidth;
  const nh = image.naturalHeight;
  const cw = image.clientWidth;
  const ch = image.clientHeight;
  if (!nw || !nh || !cw || !ch) {
    throw new Error('Image dimensions not available');
  }

  const cropL = (cw * crop.x) / 100;
  const cropT = (ch * crop.y) / 100;
  const cropW = (cw * crop.width) / 100;
  const cropH = (ch * crop.height) / 100;

  const scale = Math.min(cw / nw, ch / nh);
  const dispW = nw * scale;
  const dispH = nh * scale;
  const offX = (cw - dispW) / 2;
  const offY = (ch - dispH) / 2;

  const x1 = Math.max(cropL, offX);
  const y1 = Math.max(cropT, offY);
  const x2 = Math.min(cropL + cropW, offX + dispW);
  const y2 = Math.min(cropT + cropH, offY + dispH);

  if (x2 <= x1 || y2 <= y1) {
    throw new Error('Crop outside visible image');
  }

  return {
    x: (x1 - offX) / scale,
    y: (y1 - offY) / scale,
    w: (x2 - x1) / scale,
    h: (y2 - y1) / scale,
  };
}

export function getCroppedImg(
  image: HTMLImageElement,
  crop: PercentCropBox,
  fileName = 'profile.jpg'
): Promise<File> {
  const { x, y, w, h } = percentCropToNaturalRect(image, crop);

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w));
  canvas.height = Math.max(1, Math.round(h));
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('Canvas context not available'));

  ctx.drawImage(image, x, y, w, h, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas toBlob failed'));
          return;
        }
        const file = new File([blob], fileName, { type: 'image/jpeg' });
        resolve(file);
      },
      'image/jpeg',
      0.92
    );
  });
}
